/**
 * Channel `<select>` for a Slack recipient, fed by `GET /api/settings/slack-channels`.
 * A saved channel the list no longer contains is kept as an option (by id) so an
 * old setting still renders and can be changed deliberately.
 */
import type { SlackChannel } from './types';

export interface ChannelsState {
  status: 'loading' | 'error' | 'ready';
  channels: SlackChannel[];
}

const inputClass =
  'mt-1 block min-h-10 w-full rounded-md border border-gray-300 bg-white px-2.5 text-sm text-gray-900 aria-[invalid=true]:border-danger disabled:bg-page disabled:text-muted';

export function SlackChannelPicker({
  id,
  value,
  channels,
  error,
  onChange,
}: {
  id: string;
  value: string | null;
  channels: ChannelsState;
  error: string | undefined;
  onChange: (channelId: string | null) => void;
}) {
  const list = channels.channels;
  const known = value && !list.some((c) => c.id === value) ? [...list, { id: value, name: value }] : list;
  const empty = channels.status === 'ready' && list.length === 0;
  const hintId = `${id}-hint`;
  const errId = `${id}-err`;

  const hint =
    channels.status === 'loading'
      ? 'Loading channels…'
      : channels.status === 'error'
        ? 'Channels could not be loaded from Innu. Try again in a moment.'
        : empty
          ? 'No channels — connect Slack first.'
          : 'Alerts and nudges for this person are posted here.';

  return (
    <div className="text-[13px]">
      <label htmlFor={id} className="font-medium text-gray-800">Slack channel</label>
      <select
        id={id}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        disabled={channels.status === 'loading' || (empty && !value)}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errId : hintId}
        className={inputClass}
      >
        <option value="">{channels.status === 'loading' ? 'Loading…' : empty ? 'No channels' : 'Pick a channel'}</option>
        {known.map((c) => (
          <option key={c.id} value={c.id}>#{c.name}</option>
        ))}
      </select>
      {error ? (
        <span id={errId} role="alert" className="mt-1 block text-[12px] text-danger">{error}</span>
      ) : (
        <span id={hintId} className={`mt-1 block text-[12px] ${channels.status === 'error' ? 'text-danger' : 'text-muted'}`}>{hint}</span>
      )}
    </div>
  );
}
